import { HTTPError } from "fetch-run";
import { useCallback, useEffect, useState } from "react";

type ErrorData<T> = {
  message: string;
  errors: Record<keyof T, string[]>;
};

export type FormValue = string | number | boolean | Date | null;

export type FormValues = Record<string, FormValue>;

export type FormErrors<T> = Partial<Record<keyof T, string>>;

export type ErrorMapperResult<T> = [FormErrors<T>, string | null];

async function mapLaravelError<T extends FormValues>(
  error: HTTPError
): Promise<ErrorMapperResult<T>> {
  if (error.response.status === 422) {
    const errorData: ErrorData<T> = await error.response.json();

    const result: FormErrors<T> = {};

    for (const fieldName in errorData.errors) {
      result[fieldName] = errorData.errors[fieldName][0];
    }

    return [result, errorData.message];
  }

  return [{}, null];
}

export function useForm<T extends FormValues>(
  initialValues: T,
  error: Error | null,
  errorMapper: (
    error: HTTPError
  ) => Promise<ErrorMapperResult<T>> = mapLaravelError
) {
  // Hooks
  const [values, setValues] = useState<T>(initialValues);
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState<FormErrors<T>>({});

  useEffect(() => {
    async function main(error: HTTPError) {
      const [result, errorMessage] = await errorMapper(error);

      setErrorMessage(errorMessage ?? "");
      setErrors(result);
    }

    if (error && error instanceof HTTPError) {
      main(error);
    }
  }, [error, errorMapper, errors]);

  const setValue = useCallback(
    (name: keyof T, value: FormValue) => {
      setValues({ ...values, [name]: value });

      if (errors[name]) {
        setErrors({ ...errors, [name]: "" });
      }
    },
    [errors, values]
  );

  const setPartialValues = useCallback(
    (vals: Partial<T>) => {
      setValues({ ...values, ...vals });
    },
    [values]
  );

  // API
  return {
    values,
    errors,
    errorMessage,
    setValue,
    setValues: setPartialValues,
  };
}
